import { ChevronDown, ChevronUp, Funnel } from "lucide-react"
import { useEffect, useState } from "react"
import { useStore } from "zustand"

import type {
  CandidatePoolSortBy,
  CandidatePoolSortOrder,
  MartialArtPath,
} from "@/lib/guild-war-api"
import type { SecondaryPathFilterValue } from "@/lib/utils/candidate-pool-utils"

import { Button } from "@/components/button"
import { CardHeading } from "@/components/shared/card-heading"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { LabeledSelect } from "@/components/ui/labeled-select"
import {
  defaultCandidatePoolFilters,
  type CandidatePoolFilters,
} from "@/features/guild-war/presentation/candidate-pool-filters"
import {
  ABSENCE_FILTER_OPTIONS,
  DAY_FILTER_OPTIONS,
  PRIMARY_PATH_FILTER_OPTIONS,
  SECONDARY_PATH_FILTER_OPTIONS,
  SORT_BY_OPTIONS,
  SORT_ORDER_OPTIONS,
  SOURCE_FILTER_OPTIONS,
} from "@/features/guild-war/presentation/candidate-pool-filters"
import { useCandidatePoolRouteStore } from "@/lib/stores/candidate-pool-ui-store"

type FilterPreset = {
  id: string
  name: string
  filters: CandidatePoolFilters
}

const FILTER_PRESETS_STORAGE_KEY = "beagle:candidate-pool-filter-presets"

export function CandidatePoolFiltersSection({
  weekOptions,
  effectiveWeekId,
}: {
  weekOptions: { value: string; label: string }[]
  effectiveWeekId: string
}) {
  const store = useCandidatePoolRouteStore()
  const filters = useStore(store, (s) => s.filters)
  const candidateSearch = useStore(store, (s) => s.candidateSearch)

  const setFilter = <K extends keyof CandidatePoolFilters>(
    key: K,
    value: CandidatePoolFilters[K],
  ) =>
    store.setState((s) => ({
      page: 1,
      filters: { ...s.filters, [key]: value },
    }))

  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const [filterPresetName, setFilterPresetName] = useState("")
  const [filterPresets, setFilterPresets] = useState<FilterPreset[]>([])
  const [filterPresetMessage, setFilterPresetMessage] = useState<string | null>(
    null,
  )

  useEffect(() => {
    const timeout = globalThis.setTimeout(() => {
      store.setState({ debouncedCandidateSearch: candidateSearch.trim() })
    }, 200)

    return () => {
      globalThis.clearTimeout(timeout)
    }
  }, [candidateSearch, store])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    try {
      const raw = localStorage.getItem(FILTER_PRESETS_STORAGE_KEY)
      if (!raw) {
        return
      }

      const parsed = JSON.parse(raw) as FilterPreset[]
      if (!Array.isArray(parsed)) {
        return
      }

      const hydrated = parsed.filter(
        (entry) =>
          Boolean(entry?.id) &&
          typeof entry?.name === "string" &&
          typeof entry?.filters === "object",
      )
      setFilterPresets(hydrated)
    } catch {
      // ignore invalid localStorage payloads
    }
  }, [])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    try {
      localStorage.setItem(
        FILTER_PRESETS_STORAGE_KEY,
        JSON.stringify(filterPresets),
      )
    } catch {
      // ignore localStorage write failures
    }
  }, [filterPresets])

  const saveCurrentFiltersAsPreset = () => {
    const normalizedName = filterPresetName.trim()
    if (!normalizedName) {
      setFilterPresetMessage("Enter a preset name before saving.")
      return
    }

    const nextPreset: FilterPreset = {
      id: globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`,
      name: normalizedName,
      filters: {
        ...filters,
        paths: [...filters.paths],
      },
    }

    setFilterPresets((current) => {
      const withoutSameName = current.filter(
        (preset) => preset.name.toLowerCase() !== normalizedName.toLowerCase(),
      )
      return [...withoutSameName, nextPreset]
    })
    setFilterPresetName("")
    setFilterPresetMessage(`Saved preset "${normalizedName}".`)
  }

  const applyPreset = (preset: FilterPreset) => {
    store.setState({
      filters: { ...preset.filters, paths: [...preset.filters.paths] },
      page: 1,
    })
    setFilterPresetMessage(`Applied preset "${preset.name}".`)
  }

  const deletePreset = (presetId: string, presetName: string) => {
    setFilterPresets((current) =>
      current.filter((preset) => preset.id !== presetId),
    )
    setFilterPresetMessage(`Deleted preset "${presetName}".`)
  }

  return (
    <Card className="gap-0">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardHeading
              icon={<Funnel className="size-4" />}
              className="inline-flex"
            >
              Candidate Pool Filters
            </CardHeading>
            <CardDescription>
              Inspect candidate eligibility by day, source, skill path, and
              absence state.
            </CardDescription>
          </div>
          <Button
            unstyled
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-border/70 bg-card/70 px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-card hover:text-foreground"
            onClick={() => setIsFiltersOpen((current) => !current)}
          >
            {isFiltersOpen ? (
              <>
                Hide filters
                <ChevronUp className="size-3.5" />
              </>
            ) : (
              <>
                Show filters
                <ChevronDown className="size-3.5" />
              </>
            )}
          </Button>
        </div>
      </CardHeader>
      {isFiltersOpen ? (
        <CardContent className="grid gap-4">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="grid gap-1.5">
              <Label htmlFor="candidate-search">Search</Label>
              <Input
                id="candidate-search"
                value={candidateSearch}
                onChange={(event) => {
                  store.setState({
                    candidateSearch: event.target.value,
                    page: 1,
                  })
                }}
                placeholder="Name, ID, or path"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Week</Label>
              <LabeledSelect
                value={effectiveWeekId}
                onValueChange={(value) => {
                  setFilter("weekId", value ?? "")
                }}
                options={weekOptions}
                className="w-full"
                placeholder="Select week"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Day</Label>
              <LabeledSelect
                value={filters.day}
                onValueChange={(value) => {
                  setFilter("day", value ?? "all")
                }}
                options={DAY_FILTER_OPTIONS}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <div className="grid gap-1.5">
              <Label>Source</Label>
              <LabeledSelect
                value={filters.source}
                onValueChange={(value) => {
                  setFilter("source", value ?? "both")
                }}
                options={SOURCE_FILTER_OPTIONS}
                className="w-full"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Absence</Label>
              <LabeledSelect
                value={filters.absenceState}
                onValueChange={(value) => {
                  setFilter("absenceState", value ?? "all")
                }}
                options={ABSENCE_FILTER_OPTIONS}
                className="w-full"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Sort By</Label>
              <LabeledSelect
                value={filters.sortBy}
                onValueChange={(value) => {
                  setFilter(
                    "sortBy",
                    (value as CandidatePoolSortBy | null) ?? "member",
                  )
                }}
                options={SORT_BY_OPTIONS}
                className="w-full"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Sort Order</Label>
              <LabeledSelect
                value={filters.sortOrder}
                onValueChange={(value) => {
                  setFilter(
                    "sortOrder",
                    (value as CandidatePoolSortOrder | null) ?? "asc",
                  )
                }}
                options={SORT_ORDER_OPTIONS}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <div className="grid gap-1.5">
              <Label>Primary Path</Label>
              <LabeledSelect
                value={filters.paths[0] ?? "all"}
                onValueChange={(value) => {
                  const nextValue =
                    (value as MartialArtPath | "all" | null) ?? "all"
                  setFilter("paths", nextValue === "all" ? [] : [nextValue])
                }}
                options={PRIMARY_PATH_FILTER_OPTIONS}
                className="w-full"
              />
            </div>

            <div className="grid gap-1.5">
              <Label>Secondary Path</Label>
              <LabeledSelect
                value={filters.secondaryPath}
                onValueChange={(value) => {
                  setFilter(
                    "secondaryPath",
                    (value as SecondaryPathFilterValue | null) ?? "all",
                  )
                }}
                options={SECONDARY_PATH_FILTER_OPTIONS}
                className="w-full"
              />
            </div>
          </div>

          <div className="grid gap-3 rounded-xl border border-border/60 bg-card/35 p-3">
            <div className="text-kicker text-xs font-semibold tracking-[0.14em] text-muted-foreground">
              Filter presets
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div className="grid min-w-48 flex-1 gap-1.5">
                <Label htmlFor="filter-preset-name">Preset name</Label>
                <Input
                  id="filter-preset-name"
                  value={filterPresetName}
                  onChange={(event) => {
                    setFilterPresetName(event.target.value)
                    setFilterPresetMessage(null)
                  }}
                  placeholder="e.g. Tuesday offense"
                />
              </div>
              <Button
                type="button"
                size="sm"
                onClick={saveCurrentFiltersAsPreset}
              >
                Save preset
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  store.setState({
                    filters: { ...defaultCandidatePoolFilters },
                    page: 1,
                  })
                  setFilterPresetMessage("Reset filters to default.")
                }}
              >
                Reset filters
              </Button>
            </div>

            {filterPresets.length ? (
              <div className="flex flex-wrap gap-2">
                {filterPresets.map((preset) => (
                  <div
                    key={preset.id}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border/60 bg-background/65 px-2 py-1"
                  >
                    <Button
                      unstyled
                      type="button"
                      className="text-xs font-medium text-foreground hover:text-primary"
                      onClick={() => applyPreset(preset)}
                    >
                      {preset.name}
                    </Button>
                    <Button
                      unstyled
                      type="button"
                      className="text-xs text-muted-foreground hover:text-destructive"
                      onClick={() => deletePreset(preset.id, preset.name)}
                      aria-label={`Delete preset ${preset.name}`}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No saved presets yet.
              </p>
            )}

            {filterPresetMessage ? (
              <p className="text-xs text-muted-foreground">
                {filterPresetMessage}
              </p>
            ) : null}
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}
